import "@/App.css";
import { LangProvider } from "./i18n";
import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import api from "./api";
import { AuthProvider } from "./AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { ConfirmDialogProvider } from "./components/ConfirmDialog";
import ImpersonateBanner from "./components/ImpersonateBanner";
import Layout from "./components/Layout";
import DomainsRoutes from "./domains/DomainsRoutes";
import { LoadingSpinner } from "./components/SEOComponents";
import safeStorage from "./lib/safeStorage";
import Login from "./pages/Login";
import AcceptInvite from "./pages/AcceptInvite";
import ChangePassword from "./pages/ChangePassword";
import { tx as __tx } from "./lib/tx";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Sites = lazy(() => import("./pages/Sites"));
const Audit = lazy(() => import("./pages/Audit"));
const Keywords = lazy(() => import("./pages/Keywords"));
const Ranks = lazy(() => import("./pages/Ranks"));
const Content = lazy(() => import("./pages/Content"));
const Reports = lazy(() => import("./pages/Reports"));
const ArticleGenerator = lazy(() => import("./pages/ArticleGenerator"));
const PromotionTools = lazy(() => import("./pages/PromotionTools"));
const Automation = lazy(() => import("./pages/Automation"));
const GoogleSetup = lazy(() => import("./pages/GoogleSetup"));
const PBN = lazy(() => import("./pages/PBN"));
const Settings = lazy(() => import("./pages/Settings"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <LoadingSpinner />
    </div>
  );
}

function App() {
  const [token, setToken] = useState(() => safeStorage.getItem("xpnsr_seo_token"));
  const [user, setUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Bootstrap theme from localStorage as early as possible so Login renders
  // in the user's chosen theme (otherwise data-theme wasn't set until Sidebar
  // mounted post-login).
  useEffect(() => {
    const stored = safeStorage.getItem("xpnsr_theme");
    if (stored === "light" || stored === "dark") {
      document.documentElement.setAttribute("data-theme", stored);
    } else if (!document.documentElement.getAttribute("data-theme")) {
      document.documentElement.setAttribute("data-theme", "dark");
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (error) {
      setUser(null);
      throw error;
    }
  }, []);

  useEffect(() => {
    const validateSession = async () => {
      if (!token) {
        setUser(null);
        setSessionChecked(true);
        return;
      }

      try {
        await refreshUser();
      } catch (error) {
        safeStorage.removeItem("xpnsr_seo_token");
        setToken(null);
      } finally {
        setSessionChecked(true);
      }
    };

    setSessionChecked(false);
    validateSession();
  }, [token, refreshUser]);

  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    safeStorage.removeItem("xpnsr_seo_token");
    setToken(null);
    setSessionChecked(true);
  };

  // Audit 1.4.5: previously `return null` flashed a blank white screen between
  // first paint and the `/auth/me` round-trip. Show a centered loader so the
  // user always has feedback, even on cold cache / slow networks.
  if (!sessionChecked) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        role="status"
        aria-live="polite"
      >
        <LoadingSpinner label={__tx("Loading")} />
      </div>
    );
  }

  // Public flow: anyone with a valid invite link (signed-out OR
  // signed-in) can land on /accept-invite and set a password. Done
  // *before* the token gate so a fresh invitee isn't bounced to the
  // login form first.
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  if (pathname === "/accept-invite") {
    return (
      <ErrorBoundary>
        <LangProvider>
          <AcceptInvite onLogin={handleLogin} />
        </LangProvider>
      </ErrorBoundary>
    );
  }

  if (!token) {
    return (
      <ErrorBoundary>
        <LangProvider>
          <Login onLogin={handleLogin} />
        </LangProvider>
      </ErrorBoundary>
    );
  }

  // Forced password-change gate. When an admin resets a user's
  // password with force_change_on_next_login=true, the next /auth/me
  // returns must_change_password=true — we intercept the entire app
  // here so the user can't navigate anywhere until they finish
  // (or sign out).
  if (user?.must_change_password) {
    return (
      <ErrorBoundary>
        <LangProvider>
          <ChangePassword
            user={user}
            minPasswordLen={8}
            onChanged={refreshUser}
            onLogout={handleLogout}
          />
        </LangProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <LangProvider>
        <AuthProvider value={{ user, refresh: refreshUser }}>
          <ConfirmDialogProvider>
          <BrowserRouter>
            <ImpersonateBanner user={user} />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route element={<Layout onLogout={handleLogout} />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                  <Route path="/home" element={<Navigate to="/" replace />} />
                  <Route path="/sites" element={<Sites />} />
                  <Route path="/audit" element={<Audit />} />
                  <Route path="/keywords" element={<Keywords />} />
                  <Route path="/ranks" element={<Ranks />} />
                  <Route path="/content" element={<Content />} />
                  <Route path="/articles" element={<ArticleGenerator />} />
                  <Route path="/pbn" element={<PBN />} />
                  <Route path="/promotion" element={<PromotionTools />} />
                  <Route path="/automation" element={<Automation />} />
                  <Route path="/google-setup" element={<GoogleSetup />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/dm/*" element={<DomainsRoutes />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
          </ConfirmDialogProvider>
        </AuthProvider>
      </LangProvider>
    </ErrorBoundary>
  );
}

export default App;
